import { useMemo } from 'react'
import { PermutationFieldData } from '@global/api/data-generation'
import { Field } from '@templates/types'
import { t } from '@lingui/core/macro'
import { Badge } from './badge'
import { Lock } from 'lucide-react'

type FieldStatus =
  | { type: 'generated'; data: PermutationFieldData }
  | { type: 'excluded' }
  | { type: 'unsupported' }
  | { type: 'read-only' }

interface FieldWithStatus {
  id: number
  name: string
  status: FieldStatus
}

interface ReviewContentProps {
  prompt: string
  allFields: Field[]
  generatedFields: PermutationFieldData[]
  excludedFields: number[]
  unsupportedFields: number[]
  readOnlyFields: number[]
  isSubmitted: boolean
  showMaxPermutationsWarning: boolean
}

const PromptSection = ({ prompt }: { prompt: string }) => (
  <div className="rounded-lg border border-gray-200 p-5 bg-gray-50">
    <h3 className="text-sm font-semibold text-gray-700 mb-2">{t`Your prompt:`}</h3>
    <p className="text-gray-600 leading-relaxed">{prompt}</p>
  </div>
)

const GeneratedFieldCard = ({
  field,
  templateData,
}: {
  field: FieldWithStatus
  templateData: PermutationFieldData
}) => (
  <div className="bg-white p-4 rounded-md border border-gray-100 shadow-sm">
    <div className="font-medium text-sm text-gray-900 mb-2">{field.name}</div>
    <div className="flex flex-wrap gap-2">
      {templateData.values.map((value: string, valueIndex: number) => (
        <Badge key={`${valueIndex}-${value}`} className="bg-purple-100 text-purple-800">
          {value}
        </Badge>
      ))}
    </div>
  </div>
)

const ExcludedFieldCard = ({ field }: { field: FieldWithStatus }) => (
  <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 shadow-sm">
    <div className="font-medium text-sm text-gray-900 mb-2 flex items-center gap-2">
      {field.name}
      <Badge className="bg-gray-100 text-gray-800">{t`Not included in generation`}</Badge>
    </div>
  </div>
)

const UnsupportedFieldCard = ({ field }: { field: FieldWithStatus }) => (
  <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 shadow-sm">
    <div className="font-medium text-sm text-gray-900 mb-2 flex items-center gap-2">
      {field.name}
      <Badge className="bg-yellow-100 text-yellow-800">
        {t`Unsupported field type may be ignored`}
      </Badge>
    </div>
  </div>
)

const ReadOnlyFieldCard = ({ field }: { field: FieldWithStatus }) => (
  <div className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm">
    <div className="font-medium text-sm text-gray-900 mb-2">{field.name}</div>
    <Badge className="bg-gray-100 text-gray-800">
      <Lock className="w-4 h-4 mr-1" />
      {t`Read only field`}
    </Badge>
  </div>
)

const FieldCard = ({ field }: { field: FieldWithStatus }) => {
  const { status } = field

  switch (status.type) {
    case 'generated':
      return <GeneratedFieldCard field={field} templateData={status.data} />
    case 'excluded':
      return <ExcludedFieldCard field={field} />
    case 'unsupported':
      return <UnsupportedFieldCard field={field} />
    case 'read-only':
      return <ReadOnlyFieldCard field={field} />
    default:
      return null
  }
}

const WarningMessage = ({
  isSubmitted,
  showMaxPermutationsWarning,
}: {
  isSubmitted: boolean
  showMaxPermutationsWarning: boolean
}) => {
  if (isSubmitted) return null

  if (showMaxPermutationsWarning) {
    return (
      <div className="text-sm text-gray-600 bg-yellow-50 p-4 rounded-lg border border-yellow-100">
        {t`This request will exceed the maximum limit of 2000 rows. You may update your prompt or only the first 2000 rows will be generated.`}
      </div>
    )
  }

  return (
    <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg border border-blue-100">
      {t`Please review the generated fields and values. You can edit your prompt or confirm the results.`}
    </div>
  )
}

export const ReviewContent = ({
  prompt,
  generatedFields,
  unsupportedFields,
  excludedFields,
  allFields,
  readOnlyFields,
  isSubmitted,
  showMaxPermutationsWarning,
}: ReviewContentProps) => {
  const orderedFields = useMemo(() => {
    const fieldStatusMap = new Map<number, FieldStatus>()

    generatedFields.forEach(field => {
      fieldStatusMap.set(field.id, { type: 'generated', data: field })
    })

    excludedFields.forEach(field => {
      fieldStatusMap.set(field, { type: 'excluded' })
    })

    unsupportedFields.forEach(field => {
      fieldStatusMap.set(field, { type: 'unsupported' })
    })

    readOnlyFields.forEach(field => {
      fieldStatusMap.set(field, { type: 'read-only' })
    })

    return allFields
      .map(field => {
        const status = fieldStatusMap.get(field.id)
        return status ? { id: field.id, name: field.name, status } : null
      })
      .filter((field): field is FieldWithStatus => field !== null)
  }, [generatedFields, excludedFields, unsupportedFields, allFields])

  return (
    <div className="flex flex-col space-y-6 max-h-[600px]">
      <PromptSection prompt={prompt} />

      <div className="rounded-lg border border-gray-200 p-5 overflow-y-scroll">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{t`Template fields:`}</h3>
        <div className="space-y-4">
          {orderedFields.map((field, index) => (
            <FieldCard key={`${index}-${field.name}`} field={field} />
          ))}
        </div>
      </div>

      <WarningMessage
        isSubmitted={isSubmitted}
        showMaxPermutationsWarning={showMaxPermutationsWarning}
      />
    </div>
  )
}
